import { AUDIT_CATEGORIES } from '@napaicp/shared'
import { z } from 'zod'
import { displayNameSchema, emailSchema, publicIdSchema, utcTimestampSchema } from './primitives'

export const auditLogSchema = z.object({
  id: publicIdSchema,
  /** Null for platform-level actions such as sign-in. */
  competitionId: publicIdSchema.nullable(),
  /** Null once the acting account has been deleted; the entry itself survives. */
  actorUserId: publicIdSchema.nullable(),
  action: z.string().min(1).max(120),
  entityType: z.string().min(1).max(60),
  entityId: z.string().max(120).nullable(),
  beforeJson: z.string().nullable(),
  afterJson: z.string().nullable(),
  reason: z.string().max(500).nullable(),
  createdAt: utcTimestampSchema,
})

export type AuditLog = z.infer<typeof auditLogSchema>

/**
 * What the audit screen shows. The IP and user-agent hashes are deliberately
 * not exposed: they exist for correlation during an investigation, and
 * returning them to every competition admin would spread pseudonymous
 * identifiers around for no operational gain.
 */
export const auditLogDtoSchema = z.object({
  id: publicIdSchema,
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  /**
   * Human-readable label for the entity, where one can be resolved without a
   * denormalized copy: a team's own `name` for `entityType === 'team'`, or
   * its team's `name` for `entityType === 'submission'` (a submission has no
   * name of its own — the team is what a human recognizes it by). `null` for
   * every other entity type, a hard-deleted team/submission, or a
   * platform-level row with no `entityId`. See `AC-160`.
   */
  entityName: z.string().nullable(),
  actor: z
    .object({
      id: publicIdSchema,
      displayName: displayNameSchema,
      email: emailSchema,
    })
    .nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  reason: z.string().nullable(),
  createdAt: utcTimestampSchema,
})

export type AuditLogDto = z.infer<typeof auditLogDtoSchema>

export const auditLogQuerySchema = z.object({
  actorId: publicIdSchema.optional(),
  action: z.string().max(120).optional(),
  /** UX-108 #3/AC-160: server-side event-category filter — resolved to an `action IN (...)` clause via `actionsInCategory` (`@napaicp/shared`). */
  category: z.enum(AUDIT_CATEGORIES).optional(),
  entityType: z.string().max(60).optional(),
  entityId: z.string().max(120).optional(),
  from: utcTimestampSchema.optional(),
  to: utcTimestampSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  /** Opaque cursor: the `createdAt` of the last row from the previous page. */
  before: utcTimestampSchema.optional(),
})

export type AuditLogQuery = z.infer<typeof auditLogQuerySchema>
